// SampleDetailsPage.tsx
import { DetailPageHeader, type DetailPageAction, type DetailPageBreadcrumb } from '@/components/DetailPage';
import type { SampleWithNamesPublic } from '@/types';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Download, PencilIcon, Share2Icon } from 'lucide-react';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ProteoformAbundances } from './ProteoformAbundances';
import { SampleHeaderCard } from './SampleHeaderCard';
import { SampleRunSection } from './SampleRunSection';
import { SampleUserMetadata } from './SampleUserMetadata';
import styles from './styles.module.css';

interface SampleDetailsPageProps {
  sample: SampleWithNamesPublic;
  orgId: string;
}

const ICON = 20;

function SampleDetailsPageComponent({ sample, orgId }: SampleDetailsPageProps) {
  const { t } = useTranslation('studies');

  const crumbLabel =
    sample.assay_friendly_name !== null && sample.assay_friendly_name !== undefined && sample.assay_friendly_name !== ''
      ? `${sample.name} (${sample.assay_friendly_name})`
      : sample.name;

  const breadcrumbs: DetailPageBreadcrumb[] = [
    { id: 'samples', label: t('samples.details.backToSamples'), to: '/samples' },
    { id: 'current', label: crumbLabel },
  ];

  // Edit / Share / Download have no endpoints yet — disabled stubs with coming-soon tooltips.
  // TODO(backend): edit-sample-name, share, and per-sample download endpoints.
  const actions: DetailPageAction[] = useMemo(() => {
    const comingSoon = t('samples.details.actionComingSoon');
    return [
      {
        id: 'edit',
        label: t('samples.details.actionEdit'),
        icon: <PencilIcon size={ICON} />,
        disabled: true,
        tooltip: comingSoon,
        testId: 'edit-sample-btn',
      },
      {
        id: 'share',
        label: t('samples.details.actionShare'),
        icon: <Share2Icon size={ICON} />,
        disabled: true,
        tooltip: comingSoon,
        testId: 'share-sample-btn',
      },
      {
        id: 'download',
        label: t('samples.details.actionDownload'),
        icon: <Download size={ICON} />,
        disabled: true,
        tooltip: comingSoon,
        testId: 'download-sample-btn',
      },
    ];
  }, [t]);

  return (
    <Stack className={styles.container} fullHeight fullWidth orientation="vertical">
      <DetailPageHeader actions={actions} breadcrumbs={breadcrumbs} />
      <Stack fullWidth gap={6} orientation="vertical">
        <SampleHeaderCard sample={sample} />
        <Stack className={styles.body} fullWidth gap={6} orientation="vertical">
          <SampleRunSection orgId={orgId} sample={sample} />
          <SampleUserMetadata />
          <ProteoformAbundances orgId={orgId} sample={sample} />
        </Stack>
      </Stack>
    </Stack>
  );
}

export const SampleDetailsPage = memo(SampleDetailsPageComponent);
